import classNames from 'classnames';
import { StoryblokComponent } from 'gatsby-source-storyblok';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import {
  MARK_BOLD,
  MARK_LINK,
  MARK_STYLED,
  NODE_BR,
  NODE_HEADING,
  NODE_HR,
  NODE_IMAGE,
  NODE_LI,
  NODE_PARAGRAPH,
  NODE_QUOTE,
  NODE_UL,
  StoryblokRichtext,
  render,
} from 'storyblok-rich-text-react-renderer';
import CustomLink from '../CustomLink/CustomLink';
import Heading, { HeadingTypes } from '../Heading/Heading';
import SbImage from '../SbImage/SbImage';
import './RichText.css';

interface RichTextProps {
  content: StoryblokRichtext;
  add_padding_bottom?: boolean;
  centerResponsively?: screenWidth;
}

export enum screenWidth {
  XL = 'xl',
  LG = 'lg',
  MD = 'md',
  SM = 'sm',
}

const RichText: React.FC<RichTextProps> = ({
  content,
  add_padding_bottom,
  centerResponsively,
}) => {
  useEffect(() => {
    removeSiteDirectoryFromAnchorHrefs();
  }, []);

  const removeSiteDirectoryFromAnchorHrefs = (): void => {
    const richTextArticles: Array<Element> = Array.from(
      document.getElementsByClassName('rich-text')
    );
    richTextArticles.map((richTextArticle: Element) => {
      const anchorTags: Array<Element> = Array.from(
        richTextArticle.getElementsByTagName('a')
      ).filter(
        (anchorTag: Element) => !anchorTag.className.includes('custom-link')
      );
      anchorTags.forEach((anchorTag: any) => {
        anchorTag.href = anchorTag.href.replaceAll(
          `${process.env.GATSBY_DOMAIN_FOLDER}/`,
          ''
        );
      });
    });
  };

  const article = classNames(`rich-text prose max-w-none space-y-6`);
  const headings = classNames(
    `prose-headings:font-bold prose-headings:mb-0 text-text/body`
  );
  const blockquote = 'prose-blockquote:border-text/body';
  const p =
    'prose-p:my-0 prose-p:break-word prose-p:text-text/body prose-p:mb-2';
  const bold = 'prose-strong:font-bold';
  const ol = 'prose-ol:list-decimal text-text/body';
  const ul = 'prose-ul:list-none text-text/body';
  const li = 'prose-li:inline-block';
  return (
    <article
      className={`${article} ${headings} ${blockquote} ${p} ${bold} ${ol}`}
    >
      {render(content, {
        markResolvers: {
          [MARK_BOLD]: (children: any) => {
            return <strong>{children}</strong>;
          },
          [MARK_STYLED]: (children, { class: className }) => {
            return (
              <span
                className={classNames(className, 'block text-text/body-tint')}
              >
                {children}
              </span>
            );
          },
          [MARK_LINK]: (children: any, props: any) => {
            if (props && props?.href) {
              const { href, anchor } = props;
              const url = `${href}${anchor ? `#${anchor}` : ''}`;
              return href ? (
                <CustomLink
                  url={url}
                  label={children}
                  onClick={() => {
                    if (anchor) {
                      const element = document.getElementById(anchor);
                      if (element) element.scrollIntoView();
                    }
                  }}
                  className="no-underline hover:underline text-text/body-accent font-bold"
                >
                  {children}
                </CustomLink>
              ) : (
                children
              );
            }
          },
        },
        nodeResolvers: {
          [NODE_HEADING]: (children: any, props: any) => {
            const { level } = props;
            return (
              <Heading
                headingType={
                  level == 1
                    ? HeadingTypes.h1
                    : level == 2
                    ? HeadingTypes.h2
                    : level == 3
                    ? HeadingTypes.h3
                    : HeadingTypes.h4
                }
                text={children}
              ></Heading>
            );
          },
          [NODE_PARAGRAPH]: (children: any) => (
            <p className="mb-2">{children}</p>
          ),
          [NODE_UL]: (children: any) => (
            <div>
              <ul>{children}</ul>
            </div>
          ),
          [NODE_LI]: (children: any) => (
            <li
              className={classNames({
                'block xl:list-item': centerResponsively === screenWidth.XL,
                'block lg:list-item': centerResponsively === screenWidth.LG,
                'block md:list-item': centerResponsively === screenWidth.MD,
                'block sm:list-item': centerResponsively === screenWidth.SM,
              })}
            >
              {children}
            </li>
          ),
          [NODE_QUOTE]: (children: any) => (
            <blockquote className="space-y-2">{children}</blockquote>
          ),
          [NODE_BR]: () => <br className="!mb-0"></br>,
          [NODE_HR]: () => <hr />,
          [NODE_IMAGE]: (children, { src, alt, title }) => (
            <div className="pb-4 !mt-12 sm:!mt-16">
              {src && (
                <SbImage
                  asset={{
                    filename: src,
                    alt: alt || '',
                    title: title || '',
                    _uid: alt || '',
                    copyright: '',
                    fieldtype: '',
                    id: 869,
                    name: '',
                    focus: '',
                  }}
                  options={{ width: 800, sizes: { base: 1600 } }}
                />
              )}
            </div>
          ),
        },
        defaultBlokResolver: (name, props: any) => (
          <div
            className={classNames({
              '!my-12 sm:!my-16': add_padding_bottom,
              '!mt-12 sm:!mt-16': !add_padding_bottom,
            })}
          >
            <StoryblokComponent
              blok={{ ...props, component: name }}
              key={props.uid}
            />
          </div>
        ),
        defaultStringResolver: (str: string) => <p className="mb-2">{str}</p>,
      })}
    </article>
  );
};

RichText.defaultProps = {
  content: undefined,
};

RichText.propTypes = {
  content: PropTypes.any.isRequired,
};

export default RichText;
